import { useEffect, useState } from "react";
import { Loader2, MessageCircle, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Negotiating", "Closed", "Invalid"];
const COLORS = {
  New: "bg-orange-100 text-orange-700", Contacted: "bg-blue-100 text-blue-700",
  Negotiating: "bg-purple-100 text-purple-700", Closed: "bg-emerald-100 text-emerald-700",
  Invalid: "bg-slate-100 text-slate-500",
};

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = filter !== "all" ? { status: filter } : {};
    api.get("/admin/enquiries", { params }).then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (id, status) => { try { await api.patch(`/admin/enquiries/${id}/status`, { status }); load(); } catch { toast.error("Failed"); } };
  const remove = async (id) => { try { await api.delete(`/admin/enquiries/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };

  const waLink = (e) => {
    if (!e.whatsapp) return null;
    const num = e.whatsapp.replace(/\D/g, "");
    return `https://wa.me/${num.length === 10 ? "91" + num : num}`;
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold text-slate-900">Enquiries</h1>
        <div className="w-40">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="enquiry-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="mt-5 space-y-3">
          {items.length === 0 && <p className="py-12 text-center text-slate-500">No enquiries yet.</p>}
          {items.map((e) => (
            <div key={e.id} data-testid={`enquiry-row-${e.enquiry_code}`} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-slate-400">{e.enquiry_code}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${COLORS[e.status]}`}>{e.status}</span>
                  </div>
                  <p className="mt-0.5 font-semibold text-slate-900">{e.name}</p>
                  <p className="text-sm text-slate-500">📞 {e.mobile}{e.whatsapp && ` • WA: ${e.whatsapp}`}</p>
                  <p className="text-sm text-slate-500">{[e.city, e.state].filter(Boolean).join(", ")}</p>
                  <p className="mt-1 text-sm text-slate-700"><span className="font-semibold">Machine:</span> {e.machine_code} — {e.machine_name}</p>
                  <p className="text-sm text-slate-500"><span className="font-semibold">Seller:</span> {e.seller_name}</p>
                  {e.message && <p className="mt-1 rounded-lg bg-slate-50 p-2 text-sm text-slate-600">"{e.message}"</p>}
                  <p className="mt-1 text-xs text-slate-400">{new Date(e.created_at).toLocaleString("en-IN")}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="w-36">
                    <Select value={e.status} onValueChange={(v) => setStatus(e.id, v)}>
                      <SelectTrigger data-testid={`enquiry-status-${e.enquiry_code}`} className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    {waLink(e) && <a href={waLink(e)} target="_blank" rel="noreferrer" className="rounded-lg bg-whatsapp p-2 text-white"><MessageCircle size={15} /></a>}
                    <button onClick={() => remove(e.id)} className="rounded-lg border border-red-200 p-2 text-red-500"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
